import { RefObject, useEffect, useState } from 'react';

import isNode from 'detect-node';

export interface ElementDimensions {
  clientWidth: number;
  scrollWidth: number;
}

/**
 * Tracks a scrollable element's `clientWidth`/`scrollWidth` as state, re-measuring on
 * mount, on resize (via `ResizeObserver`), and whenever `deps` change (e.g. children).
 *
 * Returns `null` until the first measurement so callers can distinguish "not measured
 * yet" from a genuinely empty (0-width) element. Reading these dimensions from state
 * — rather than from the ref during render — keeps derived values (page counts, current
 * page) reactive without accessing `ref.current` while rendering.
 */
export function useElementDimensions(
  elementRef: RefObject<HTMLElement | null> | null,
  deps: unknown[] = [],
): ElementDimensions | null {
  const [dimensions, setDimensions] = useState<ElementDimensions | null>(null);

  useEffect(() => {
    const element = elementRef?.current ?? null;
    if (isNode || !element) {
      return;
    }

    const measure = (): void => {
      setDimensions({ clientWidth: element.clientWidth, scrollWidth: element.scrollWidth });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, ...deps]);

  return dimensions;
}
